import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../flux/stores/hooks';
import {
    fetchOrganizationMembers,
    fetchOrganizationInvitations,
    createOrganizationInvitation,
    revokeOrganizationInvitation,
    removeOrganizationMember,
    updateOrganizationMemberRole,
} from '../../flux/actions/organizationThunks';
import { fetchCurrentUser } from '../../flux/actions/userThunks';
import { useApiAccessToken } from '../../utils/useApiAccessToken';

export const OrganizationMembers = () => {
    const dispatch = useAppDispatch();
    const { getAccessToken } = useApiAccessToken();
    const currentUser = useAppSelector(state => state.user.current.data);
    const userLoading = useAppSelector(state => state.user.current.isLoading);
    const organizationLoading = useAppSelector(state => state.organization.isLoading);
    const members = useAppSelector(state => state.organization.members);
    const invitations = useAppSelector(state => state.organization.invitations) ?? {
        data: [], status: 'idle', error: null, creating: false, revokingInvitationIds: [],
    };
    const [memberToRemove, setMemberToRemove] = useState(null);
    const [invitationToRevoke, setInvitationToRevoke] = useState(null);
    const [accessDenied, setAccessDenied] = useState(false);
    const [invitationAccessDenied, setInvitationAccessDenied] = useState(false);
    const [inviteFormOpen, setInviteFormOpen] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('member');
    const [inviteFormError, setInviteFormError] = useState(null);
    const [inviteEmailError, setInviteEmailError] = useState(null);
    const [invitationNotice, setInvitationNotice] = useState(null);
    const isAdmin = currentUser?.org_role === 'admin';

    const loadMembers = useCallback(async () => {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return;

            const result = await dispatch(fetchOrganizationMembers({ accessToken }));
            setAccessDenied(
                fetchOrganizationMembers.rejected.match(result) && result.payload?.status === 403
            );
        } catch (error) {
            console.error('Failed to acquire API token for organization members:', error);
        }
    }, [dispatch, getAccessToken]);

    const loadInvitations = useCallback(async () => {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return;

            const result = await dispatch(fetchOrganizationInvitations({ accessToken }));
            setInvitationAccessDenied(
                fetchOrganizationInvitations.rejected.match(result) && result.payload?.status === 403
            );
        } catch {
            setInvitationNotice('Unable to load invitations. Please try again.');
        }
    }, [dispatch, getAccessToken]);

    useEffect(() => {
        if (!isAdmin) return;
        loadMembers();
        loadInvitations();
    }, [isAdmin, loadInvitations, loadMembers]);

    const refreshCurrentUserIfNeeded = async (accessToken, userObjectId) => {
        if (currentUser?.object_id === userObjectId) {
            await dispatch(fetchCurrentUser({ accessToken }));
        }
    };

    const handleRoleChange = async (member, role) => {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return;
            const result = await dispatch(updateOrganizationMemberRole({ accessToken, userObjectId: member.user_object_id, role }));
            if (updateOrganizationMemberRole.fulfilled.match(result)) {
                await refreshCurrentUserIfNeeded(accessToken, member.user_object_id);
            }
        } catch (error) {
            console.error('Failed to update organization member role:', error);
        }
    };

    const handleRemove = async () => {
        if (!memberToRemove) return;
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return;
            const result = await dispatch(removeOrganizationMember({ accessToken, userObjectId: memberToRemove.user_object_id }));
            if (removeOrganizationMember.fulfilled.match(result)) {
                await refreshCurrentUserIfNeeded(accessToken, memberToRemove.user_object_id);
                setMemberToRemove(null);
            }
        } catch (error) {
            console.error('Failed to remove organization member:', error);
        }
    };

    const closeInviteForm = () => {
        setInviteFormOpen(false);
        setInviteFormError(null);
        setInviteEmailError(null);
    };

    const handleInviteSubmit = async (event) => {
        event.preventDefault();
        const email = inviteEmail.trim().toLowerCase();
        setInviteFormError(null);
        setInviteEmailError(null);
        setInvitationNotice(null);

        if (!email) {
            setInviteEmailError('Enter an email address.');
            return;
        }

        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return;
            const result = await dispatch(createOrganizationInvitation({ accessToken, email, role: inviteRole }));
            if (createOrganizationInvitation.fulfilled.match(result)) {
                setInvitationNotice(`Invitation sent to ${result.payload.invited_email}.`);
                setInviteEmail('');
                setInviteRole('member');
                closeInviteForm();
                return;
            }

            const status = result.payload?.status;
            if (status === 403) {
                setInvitationAccessDenied(true);
            } else if (status === 409) {
                setInviteFormError(result.payload?.message ?? 'An invitation already exists for this email address.');
            } else if (status === 422) {
                setInviteEmailError(result.payload?.message ?? 'Enter a valid email address.');
            } else if (status === 429) {
                setInviteFormError('Too many invitation requests. Please try again later.');
            } else {
                setInviteFormError('Unable to send the invitation. Please try again.');
            }
        } catch {
            setInviteFormError('Unable to send the invitation. Please try again.');
        }
    };

    const handleRevoke = async () => {
        if (!invitationToRevoke) return;
        setInvitationNotice(null);
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return;
            const result = await dispatch(revokeOrganizationInvitation({
                accessToken,
                invitationObjectId: invitationToRevoke.object_id,
            }));
            if (revokeOrganizationInvitation.fulfilled.match(result)) {
                setInvitationNotice(`Invitation to ${invitationToRevoke.invited_email} revoked.`);
                setInvitationToRevoke(null);
                return;
            }
            if (result.payload?.status === 404) {
                setInvitationNotice('This invitation is no longer pending.');
                setInvitationToRevoke(null);
                await loadInvitations();
            }
        } catch {
            setInvitationNotice('Unable to revoke the invitation. Please try again.');
        }
    };

    if (userLoading || organizationLoading || !currentUser) {
        return <p className="projects-loading">Loading organization members…</p>;
    }
    if (!isAdmin || accessDenied) {
        return <div className="org-error members-error" role="alert">You do not have permission to manage members.</div>;
    }

    return (
        <div>
            <div className="members-toolbar">
                <button type="button" className="rules-btn-ghost" onClick={() => { loadMembers(); loadInvitations(); }} disabled={members.status === 'loading' || invitations.status === 'loading'}>Refresh</button>
            </div>

            {members.error && <div className="org-error members-error" role="alert"><span>{members.error}</span><button type="button" className="rules-btn-ghost" onClick={loadMembers}>Retry</button></div>}

            {members.status === 'loading' && members.data.length === 0 ? (
                <p className="projects-loading">Loading active members…</p>
            ) : members.data.length === 0 ? (
                <div className="rules-empty"><p>No active members found.</p></div>
            ) : (
                <div className="rules-table-wrapper">
                    <table className="rules-table members-table">
                        <thead><tr><th className="rules-th">Name</th><th className="rules-th">Email</th><th className="rules-th">Role</th><th className="rules-th">Status</th><th className="rules-th">Actions</th></tr></thead>
                        <tbody>{members.data.map((member) => {
                            const isCurrentUser = member.user_object_id === currentUser.object_id;
                            const isUpdating = members.updatingMemberIds.includes(member.user_object_id);
                            const isRemoving = members.removingMemberIds.includes(member.user_object_id);
                            const busy = isUpdating || isRemoving;
                            return <tr key={member.user_object_id} className="rules-tr">
                                <td className="rules-td rules-td-name">{member.display_name || member.email}{isCurrentUser && <span className="members-you">You</span>}</td>
                                <td className="rules-td">{member.email}</td>
                                <td className="rules-td"><select className="members-role-select" aria-label={`Role for ${member.email}`} value={member.org_role} disabled={busy} onChange={(event) => handleRoleChange(member, event.target.value)}><option value="admin">Admin</option><option value="member">Member</option></select></td>
                                <td className="rules-td"><span className="members-status">Active</span></td>
                                <td className="rules-td"><button type="button" className="members-remove-button" disabled={busy} onClick={() => setMemberToRemove(member)}>{isRemoving ? 'Removing…' : 'Remove'}</button></td>
                            </tr>;
                        })}</tbody>
                    </table>
                </div>
            )}

            {memberToRemove && <div className="members-modal-backdrop" role="presentation"><div className="members-modal" role="dialog" aria-modal="true" aria-labelledby="remove-member-title"><h2 id="remove-member-title">Remove {memberToRemove.display_name || memberToRemove.email}?</h2><p>They will lose access to projects in this organization. Their account will not be deleted.</p><div className="pw-actions"><button type="button" className="rules-btn-ghost" onClick={() => setMemberToRemove(null)}>Cancel</button><button type="button" className="members-remove-button" onClick={handleRemove}>Remove member</button></div></div></div>}

            <section className="members-invitations" aria-labelledby="invitations-title">
                <div className="members-invitations-header">
                    <div>
                        <h2 id="invitations-title">Invitations</h2>
                        <p>Invite people to join this organization and manage pending invitations.</p>
                    </div>
                    {!invitationAccessDenied && <button type="button" className="rules-btn-primary" onClick={() => setInviteFormOpen(true)}>Invite member</button>}
                </div>

                {invitationNotice && <div className="members-invitation-notice" role="status">{invitationNotice}</div>}
                {invitationAccessDenied ? (
                    <div className="org-error members-error" role="alert">You do not have permission to manage invitations.</div>
                ) : (
                    <>
                        {invitations.error && !inviteFormOpen && <div className="org-error members-error" role="alert"><span>{invitations.error}</span><button type="button" className="rules-btn-ghost" onClick={loadInvitations}>Retry</button></div>}
                        {invitations.status === 'loading' && invitations.data.length === 0 ? (
                            <p className="projects-loading">Loading pending invitations…</p>
                        ) : invitations.data.length === 0 ? (
                            <div className="rules-empty"><p>No pending invitations.</p></div>
                        ) : (
                            <div className="rules-table-wrapper">
                                <table className="rules-table members-table invitations-table">
                                    <thead><tr><th className="rules-th">Invitee email</th><th className="rules-th">Role</th><th className="rules-th">Sent</th><th className="rules-th">Expires</th><th className="rules-th">Status</th><th className="rules-th">Actions</th></tr></thead>
                                    <tbody>{invitations.data.map((invitation) => {
                                        const isRevoking = invitations.revokingInvitationIds.includes(invitation.object_id);
                                        return <tr key={invitation.object_id} className="rules-tr">
                                            <td className="rules-td">{invitation.invited_email}</td>
                                            <td className="rules-td">{invitation.role}</td>
                                            <td className="rules-td">{new Date(invitation.created_at).toLocaleString()}</td>
                                            <td className="rules-td">{new Date(invitation.expires_at).toLocaleString()}</td>
                                            <td className="rules-td"><span className="members-status">{invitation.status}</span></td>
                                            <td className="rules-td"><button type="button" className="members-remove-button" disabled={isRevoking} onClick={() => setInvitationToRevoke(invitation)}>{isRevoking ? 'Revoking…' : 'Revoke'}</button></td>
                                        </tr>;
                                    })}</tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </section>

            {inviteFormOpen && <div className="members-modal-backdrop" role="presentation"><div className="members-modal" role="dialog" aria-modal="true" aria-labelledby="invite-member-title"><h2 id="invite-member-title">Invite member</h2><form onSubmit={handleInviteSubmit}><label className="pw-label" htmlFor="invite-email">Email</label><input id="invite-email" type="email" className="pw-input" value={inviteEmail} onChange={(event) => setInviteEmail(event.target.value)} aria-invalid={Boolean(inviteEmailError)} aria-describedby={inviteEmailError ? 'invite-email-error' : undefined} autoFocus />{inviteEmailError && <p id="invite-email-error" className="members-field-error" role="alert">{inviteEmailError}</p>}<label className="pw-label" htmlFor="invite-role">Role</label><select id="invite-role" className="members-role-select" value={inviteRole} onChange={(event) => setInviteRole(event.target.value)}><option value="member">Member</option><option value="admin">Admin</option></select>{inviteRole === 'admin' && <p className="members-admin-warning" role="note">Admins receive full organization-administration privileges when they accept.</p>}{inviteFormError && <p className="members-field-error" role="alert">{inviteFormError}</p>}<div className="pw-actions"><button type="button" className="rules-btn-ghost" onClick={closeInviteForm} disabled={invitations.creating}>Cancel</button><button type="submit" className="rules-btn-primary" disabled={invitations.creating}>{invitations.creating ? 'Sending…' : 'Send invitation'}</button></div></form></div></div>}
            {invitationToRevoke && <div className="members-modal-backdrop" role="presentation"><div className="members-modal" role="dialog" aria-modal="true" aria-labelledby="revoke-invitation-title"><h2 id="revoke-invitation-title">Revoke invitation to {invitationToRevoke.invited_email}?</h2><p>They will no longer be able to use this invitation link. To invite them again, create a new invitation.</p><div className="pw-actions"><button type="button" className="rules-btn-ghost" onClick={() => setInvitationToRevoke(null)}>Cancel</button><button type="button" className="members-remove-button" onClick={handleRevoke}>Revoke invitation</button></div></div></div>}
        </div>
    );
};

export default OrganizationMembers;
